(() => {
  const isThemeEditor =
    window.Shopify?.designMode === true ||
    document.documentElement.classList.contains('shopify-design-mode');

  if (isThemeEditor) return;

  const languageSelector = 'select[data-nr-language-navigation]';
  const languageMenuSelector = '[data-nr-language-menu]';
  const languageTriggerSelector = '[data-nr-language-trigger]';
  const languageOptionSelector = '[data-nr-language-option]';
  const defaultLanguage = 'en';
  const languagePreferenceKey = 'nexridge:language:v1';
  const languagePreferenceCookie = 'nexridge_language';
  const localPreviewHosts = new Set(['127.0.0.1', 'localhost', '0.0.0.0', '::1', '[::1]']);

  function normalizeRoot(value) {
    const pathname = new URL(value || '/', window.location.origin).pathname;

    if (pathname === '/') return '/';

    return `/${pathname.replace(/^\/+|\/+$/g, '')}/`;
  }

  function buildLanguageUrl(select, option = select.selectedOptions[0]) {
    const targetRootValue = option?.dataset.rootUrl;

    if (!targetRootValue) return null;

    const currentRoot = normalizeRoot(select.dataset.currentRootUrl || '/');
    const targetRoot = normalizeRoot(targetRootValue);
    const currentPath = window.location.pathname;
    const currentHome = currentRoot === '/' ? '/' : currentRoot.slice(0, -1);
    let remainder = '';

    if (currentPath === currentHome || currentPath === currentRoot) {
      remainder = '';
    } else if (currentRoot === '/') {
      remainder = currentPath.replace(/^\/+/, '');
    } else if (currentPath.startsWith(currentRoot)) {
      remainder = currentPath.slice(currentRoot.length);
    }

    const targetHome = targetRoot === '/' ? '/' : targetRoot.slice(0, -1);
    const targetPath = remainder ? `${targetRoot}${remainder}` : targetHome;

    return `${targetPath}${window.location.search}${window.location.hash}`;
  }

  function readLanguagePreference() {
    try {
      const storedLanguage = window.localStorage.getItem(languagePreferenceKey);

      if (storedLanguage) return storedLanguage;
    } catch (_error) {
      // Continue with the first-party cookie fallback.
    }

    const cookiePrefix = `${languagePreferenceCookie}=`;
    const preferenceCookie = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith(cookiePrefix));

    if (!preferenceCookie) return null;

    try {
      return decodeURIComponent(preferenceCookie.slice(cookiePrefix.length));
    } catch (_error) {
      return preferenceCookie.slice(cookiePrefix.length);
    }
  }

  function saveLanguagePreference(languageCode) {
    try {
      window.localStorage.setItem(languagePreferenceKey, languageCode);
    } catch (_error) {
      // The cookie below still preserves the buyer's explicit choice.
    }

    document.cookie = `${languagePreferenceCookie}=${encodeURIComponent(
      languageCode
    )}; path=/; max-age=31536000; SameSite=Lax`;
  }

  function findLanguageOption(select, languageCode) {
    const normalizedLanguageCode = languageCode.toLowerCase();

    return Array.from(select.options).find(
      (option) => option.value.toLowerCase() === normalizedLanguageCode
    );
  }

  function getOpenLanguageMenus() {
    return document.querySelectorAll(`${languageMenuSelector}.is-open`);
  }

  function getLanguageMenuItems(menu) {
    return Array.from(menu.querySelectorAll(languageOptionSelector));
  }

  function closeLanguageMenu(menu, restoreFocus = false) {
    const trigger = menu.querySelector(languageTriggerSelector);
    const options = menu.querySelector('[data-nr-language-options]');

    menu.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');

    if (options instanceof HTMLElement) options.hidden = true;
    if (restoreFocus && trigger instanceof HTMLElement) trigger.focus();
  }

  function closeOtherLanguageMenus(exception) {
    for (const menu of getOpenLanguageMenus()) {
      if (menu !== exception) closeLanguageMenu(menu);
    }
  }

  function openLanguageMenu(menu, focusPosition = 'current') {
    const trigger = menu.querySelector(languageTriggerSelector);
    const options = menu.querySelector('[data-nr-language-options]');
    const items = getLanguageMenuItems(menu);

    closeOtherLanguageMenus(menu);
    menu.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');

    if (options instanceof HTMLElement) options.hidden = false;
    if (!items.length) return;

    let target = items.find((item) => item.getAttribute('aria-checked') === 'true');

    if (focusPosition === 'first') target = items[0];
    if (focusPosition === 'last') target = items[items.length - 1];

    target?.focus();
  }

  function initializeDefaultLanguage() {
    const select = document.querySelector(languageSelector);

    if (!(select instanceof HTMLSelectElement)) return;

    const currentLanguage = (select.dataset.currentLanguage || '').toLowerCase();
    const preferredLanguage = (readLanguagePreference() || '').toLowerCase();

    if (currentLanguage === defaultLanguage) {
      if (!preferredLanguage) saveLanguagePreference(defaultLanguage);
      return;
    }

    // Preserve an explicit non-English choice, especially Simplified Chinese.
    if (preferredLanguage && preferredLanguage !== defaultLanguage) return;

    const englishOption = findLanguageOption(select, defaultLanguage);

    if (!englishOption) return;

    saveLanguagePreference(defaultLanguage);

    const targetUrl = buildLanguageUrl(select, englishOption);

    if (targetUrl) window.location.replace(targetUrl);
  }

  document.addEventListener('change', (event) => {
    const select = event.target;

    if (
      !(select instanceof HTMLSelectElement) ||
      !select.matches(languageSelector) ||
      select.value === select.dataset.currentLanguage
    ) {
      return;
    }

    saveLanguagePreference(select.value);

    const isLocalPreview =
      localPreviewHosts.has(window.location.hostname) || window.location.hostname.endsWith('.localhost');

    if (!isLocalPreview && select.form) {
      select.form.submit();
      return;
    }

    const targetUrl = buildLanguageUrl(select);

    if (targetUrl) window.location.assign(targetUrl);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const optionButton = event.target.closest(languageOptionSelector);

    if (optionButton instanceof HTMLButtonElement) {
      const menu = optionButton.closest(languageMenuSelector);
      const select = menu?.querySelector(languageSelector);
      const languageCode = optionButton.dataset.languageCode;

      if (
        !(menu instanceof HTMLElement) ||
        !(select instanceof HTMLSelectElement) ||
        !languageCode
      ) {
        return;
      }

      if (languageCode === select.dataset.currentLanguage) {
        closeLanguageMenu(menu, true);
        return;
      }

      const nativeOption = findLanguageOption(select, languageCode);

      if (!nativeOption) return;

      closeLanguageMenu(menu, true);
      select.value = languageCode;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const trigger = event.target.closest(languageTriggerSelector);

    if (trigger instanceof HTMLButtonElement) {
      const menu = trigger.closest(languageMenuSelector);

      if (!(menu instanceof HTMLElement)) return;

      if (menu.classList.contains('is-open')) {
        closeLanguageMenu(menu, true);
      } else {
        openLanguageMenu(menu);
      }
      return;
    }

    for (const menu of getOpenLanguageMenus()) closeLanguageMenu(menu);
  });

  document.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(languageTriggerSelector);

    if (trigger) {
      const menu = trigger.closest(languageMenuSelector);

      if (!(menu instanceof HTMLElement)) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openLanguageMenu(menu, event.key === 'ArrowDown' ? 'first' : 'last');
      } else if (event.key === 'Escape' && menu.classList.contains('is-open')) {
        event.preventDefault();
        closeLanguageMenu(menu, true);
      }
      return;
    }

    const activeItem = event.target.closest(languageOptionSelector);

    if (!(activeItem instanceof HTMLButtonElement)) return;

    const menu = activeItem.closest(languageMenuSelector);

    if (!(menu instanceof HTMLElement)) return;

    const items = getLanguageMenuItems(menu);
    const currentIndex = items.indexOf(activeItem);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % items.length;
        break;
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + items.length) % items.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = items.length - 1;
        break;
      case 'Escape':
        event.preventDefault();
        closeLanguageMenu(menu, true);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        activeItem.click();
        return;
      default:
        return;
    }

    event.preventDefault();
    items[nextIndex]?.focus();
  });

  document.addEventListener('focusin', (event) => {
    for (const menu of getOpenLanguageMenus()) {
      if (!menu.contains(event.target)) closeLanguageMenu(menu);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDefaultLanguage, { once: true });
  } else {
    initializeDefaultLanguage();
  }
})();
